import { useState, useRef, useCallback } from "react"
import { useSelector } from "react-redux"
import { message } from "antd"
import { Store } from "../Apis/storeApis"
import { getUserCookies } from "../utils/Cookies"
import { jwtDecode } from "jwt-decode"
import { translations } from "../utils/Translation/staticTranslation"
import { effectiveHighestRole } from "../utils/modulePermissionSidebar"

const useStoreActions = (setDeleteModalVisible) => {
  const lang = localStorage.getItem("language") || "ar"
  const profileUser = useSelector((state) => state.user.user)

  const [stores, setStores] = useState([])
  const [loading, setLoading] = useState(false)
  const [deleteStore, setDeleteStore] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    next: null,
    previous: null,
  })
  const token = getUserCookies()?.token
  const decoded = token ? jwtDecode(token) : null
  const highest_role = decoded?.highest_role
  const market_role = decoded?.market_role
  const effectiveHighest = effectiveHighestRole(profileUser, highest_role)
  const canFilterByBusiness =
    effectiveHighest === "SUPERADMIN" ||
    market_role === "2" ||
    market_role === "3"
  const common_error_msg = translations[lang]?.common_error_msg

  /** Monotonic id so slower/outdated list responses cannot overwrite a newer request (role/profile race or tab switch). */
  const storesListRequestIdRef = useRef(0)

  const prevFetchParams = useRef({
    roleKey: null,
    merchant: null,
    category: "",
    searchTerm: "",
    filterStatus: "",
    requestStatus: null,
  })

  const buildApprovedStorePayload = useCallback(
    (selectMerchant, selectedCategory, searchTerm, filterStatus) => {
      const payload = {}
      if (selectedCategory === "Name" && searchTerm) {
        payload.name = searchTerm
      } else if (selectedCategory === "Type" && searchTerm) {
        payload.type = searchTerm
      }

      if (filterStatus) {
        payload.status = filterStatus
      }

      // Only add business filter if a specific merchant is selected (SUPERADMIN or market_role 2)
      if (
        selectMerchant &&
        selectMerchant.id &&
        canFilterByBusiness
      ) {
        payload.business = selectMerchant.id
      }

      return payload
    },
    [canFilterByBusiness]
  )

  const fetchApprovedStore = useCallback(
    async (
      selectMerchant = null,
      selectedCategory = "",
      searchTerm = "",
      filterStatus = "",
      page = 1
    ) => {
      const currentParams = {
        roleKey: effectiveHighest ?? highest_role ?? "",
        merchant: selectMerchant?.id || null,
        category: selectedCategory,
        searchTerm,
        filterStatus,
        requestStatus: null,
        page,
      }

      // Check if we've already fetched with these params
      // Always allow fetch if page is different
      const prevPage = prevFetchParams.current?.page || 1
      if (
        page === prevPage &&
        JSON.stringify(currentParams) ===
          JSON.stringify(prevFetchParams.current)
      ) {
        return
      }

      prevFetchParams.current = currentParams

      const requestId = ++storesListRequestIdRef.current
      setLoading(true)
      const payload = buildApprovedStorePayload(
        selectMerchant,
        selectedCategory,
        searchTerm,
        filterStatus
      )

      // Add page parameter
      payload.page = page

      try {
        const response = await Store.getApprovedStores(payload)
        if (requestId !== storesListRequestIdRef.current) return
        if (response.status === 200) {
          const data = response.data
          // Handle both paginated and non-paginated responses
          setStores(data.results || data || [])

          // Update pagination state if paginated response
          if (data.count !== undefined) {
            setPagination({
              current: page,
              pageSize: 10,
              total: data.count || 0,
              next: data.next || null,
              previous: data.previous || null,
            })
          }
        }
      } catch (error) {
        if (requestId !== storesListRequestIdRef.current) return
        setStores([])
        message.error(common_error_msg?.failed_to_fetch_approved_stores)
        console.error("Error:", error)
      } finally {
        if (requestId === storesListRequestIdRef.current) {
          setLoading(false)
        }
      }
    },
    [buildApprovedStorePayload, common_error_msg, effectiveHighest, highest_role]
  )

  const fetchOtherStores = useCallback(
    async (
      requestStatus,
      selectMerchant = null,
      selectedCategory = "",
      searchTerm = "",
      page = 1
    ) => {
      const currentParams = {
        roleKey: effectiveHighest ?? highest_role ?? "",
        merchant: selectMerchant?.id || null,
        category: selectedCategory,
        searchTerm,
        filterStatus: "",
        requestStatus,
        page,
      }

      // Check if we've already fetched with these params
      // Always allow fetch if page is different
      const prevPage = prevFetchParams.current?.page || 1
      if (
        page === prevPage &&
        JSON.stringify(currentParams) ===
          JSON.stringify(prevFetchParams.current)
      ) {
        return
      }

      prevFetchParams.current = currentParams

      const requestId = ++storesListRequestIdRef.current
      setLoading(true)
      // requestStatus can be 1 for pending or 3 for rejected
      const payload = {
        request_status: requestStatus,
        request_type: "Add Store", // Required by API
      }

      if (selectedCategory === "Name" && searchTerm) {
        payload.name = searchTerm
      } else if (selectedCategory === "Type" && searchTerm) {
        payload.type = searchTerm
      }

      // Only add business filter if a specific merchant is selected (SUPERADMIN or market_role 2)
      if (
        selectMerchant &&
        selectMerchant.id &&
        canFilterByBusiness
      ) {
        payload.business = selectMerchant.id
      }

      // Add page parameter
      payload.page = page

      try {
        const response = await Store.getOtherStores(payload)
        if (requestId !== storesListRequestIdRef.current) return
        if (response.status === 200) {
          const data = response.data
          // Handle both paginated and non-paginated responses
          const rawStores = data.results || (Array.isArray(data) ? data : [])

          // Map API response fields to match table column expectations
          // For pending stores, use store_name directly (not business_name)
          const mappedStores = rawStores.map((store) => ({
            ...store,
            // Use store_name directly for pending stores (fallback to business_name if store_name doesn't exist)
            store_name: store.store_name || store.business_name,
            store_name_arabic:
              store.store_name_arabic || store.business_name_arabic,
            // Map e_commerce_plateform to provider for table display
            provider: store.e_commerce_plateform || store.provider,
            provider_arabic:
              store.e_commerce_plateform_arabic || store.provider_arabic,
          }))

          setStores(mappedStores)

          // Update pagination state if paginated response
          if (data.count !== undefined) {
            setPagination({
              current: page,
              pageSize: 10,
              total: data.count || 0,
              next: data.next || null,
              previous: data.previous || null,
            })
          }
        }
      } catch (error) {
        if (requestId !== storesListRequestIdRef.current) return
        setStores([])
        message.error(
          common_error_msg?.failed_to_fetch_stores || "Failed to fetch stores"
        )
        console.error("Error fetching other stores:", error)
      } finally {
        if (requestId === storesListRequestIdRef.current) {
          setLoading(false)
        }
      }
    },
    [canFilterByBusiness, common_error_msg, effectiveHighest, highest_role]
  )

  const handleDelete = useCallback(
    async (storeId) => {
      try {
        setDeleteStore(true)
        const response = await Store.deleteStore(storeId)
        if (response.status === 204) {
          message.success(common_error_msg?.store_deleted_successfully)
          setDeleteModalVisible(false)
          setStores((prevStores) =>
            prevStores.filter((store) => store.id !== storeId)
          )
        }
      } catch (error) {
        message.error(common_error_msg?.failed_to_delete_store)
        console.error("Error deleting store:", error)
      } finally {
        setDeleteStore(false)
      }
    },
    [setDeleteModalVisible]
  )

  const handleDeleteRequest = useCallback(
    async (storeId) => {
      try {
        setDeleteStore(true)
        const response = await Store.deleteRequest(storeId)
        if (response.status === 200) {
          message.success(common_error_msg?.request_deleted_successfully)
          setDeleteModalVisible(false)
          setStores((prevStores) =>
            prevStores.filter((store) => store.id !== storeId)
          )
        }
      } catch (error) {
        message.error(common_error_msg?.failed_to_delete_store)
        console.error("Error deleting store:", error)
      } finally {
        setDeleteStore(false)
      }
    },
    [setDeleteModalVisible]
  )

  return {
    stores,
    setStores,
    loading,
    fetchApprovedStore,
    fetchOtherStores,
    handleDelete,
    handleDeleteRequest,
    deleteStore,
    pagination,
    setPagination,
  }
}

export default useStoreActions
